/**
 * <AuroraFill> — l'aurore des champions (molecule, Brand core V16 chap. 03 et 06).
 *
 * Les cinq couleurs du cadre des temps forts — le jaune, puis Zoé, Tina, Sam et Abou —, cette
 * fois en calque très léger (16 à 24 %), à 120°, qui teinte TOUTE une surface. Elle dit « un
 * champion a agi ici » : Zoé a résumé le bilan, Sam a préparé la séance.
 *
 *  - ✅ l'élément où un champion a agi : la carte du dernier bilan, une pilule de préparation ;
 *  - ❌ un fond du quotidien, une liste, un bouton, un texte, le logo. Ce n'est pas une façon de
 *    distinguer une carte : c'est la trace d'un champion.
 *
 * En code : un calque posé en ABSOLU en premier enfant de la surface, qui en remplit toute la
 * boîte (padding compris) et en suit le rayon. La surface garde son aplat — blanc, ou nuit
 * `#152452` en sombre —, et l'aurore se pose dessus, comme le livre l'écrit. Le style de position
 * vit sur une `<View>` : `<LinearGradient>` n'est pas patché par Unistyles, une feuille du thème
 * posée sur lui n'est jamais résolue. Le dégradé mesure sa boîte pour tenir 120° sur toute forme.
 */
import React from "react"
import { StyleSheet as RNStyleSheet, View } from "react-native"
import { LinearGradient } from "expo-linear-gradient"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { useGradientAnchors } from "../../hooks/useGradientAnchors"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { championRamp } from "../../theme/tokens/effects"

/** Les cinq couleurs du dégradé des champions, en calque — arrêts et angle : `championRamp`. */
const AURORA_COLORS: SemanticColorKey[] = ["aurora-accent", "aurora-zoe", "aurora-tina", "aurora-sam", "aurora-abou"]

export type AuroraFillProps = {
    /** Rayon de la surface qu'elle remplit, en px. Défaut : le rayon d'une carte (`radius.lg`). */
    radius?: number
    testID?: string
}

export function AuroraFill(props: AuroraFillProps) {
    const { radius, testID } = props
    const { theme } = useUnistyles()
    const { anchors, onLayout } = useGradientAnchors(championRamp.angle)

    return (
        <View testID={testID} style={styles.fill(radius ?? theme.radius.lg)} pointerEvents="none" onLayout={onLayout}>
            <LinearGradient
                colors={AURORA_COLORS.map((key) => theme.colors[key]) as [string, string, ...string[]]}
                locations={championRamp.stops}
                start={anchors.start}
                end={anchors.end}
                style={RNStyleSheet.absoluteFill}
            />
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    fill: (radius: number) => ({
        ...RNStyleSheet.absoluteFillObject,
        borderRadius: radius,
        overflow: "hidden" as const,
    }),
}))
